/**
 * Conversation memory for one campaign's assistant screen: the turns asked
 * so far, persisted to `sessionStorage` so a reload keeps the transcript —
 * never to a server, per AS-2's own constraint that nothing here is logged.
 *
 * A turn that never saw `done` or `refusal` — the stream cut by a deploy or
 * a dropped connection (platform design §12.3) — is `interrupted`, not an
 * error: the question is kept and `askAgain` resends it unchanged. A turn
 * still `streaming` when the page unloads reads back as `interrupted` too,
 * since nothing can resume a stream nobody kept open.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { openTurnStream } from './stream';
import { turnEventSchema, type TurnEvent, type TurnRequest, type TurnWindow } from './schemas';

export type TurnStatus = 'streaming' | 'done' | 'refused' | 'interrupted';

export interface TurnRecord {
  id: string;
  question: string;
  events: TurnEvent[];
  status: TurnStatus;
}

const turnRecordSchema = z.object({
  id: z.string(),
  question: z.string(),
  events: z.array(turnEventSchema),
  status: z.enum(['streaming', 'done', 'refused', 'interrupted']),
});

const STORAGE_KEY_PREFIX = 'socialsrc.assistant.turns.';

function readTurns(campaignId: string): TurnRecord[] {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${campaignId}`);
    if (raw === null) return [];
    const parsed = z.array(turnRecordSchema).safeParse(JSON.parse(raw));
    if (!parsed.success) return [];
    return parsed.data.map((turn) =>
      turn.status === 'streaming' ? { ...turn, status: 'interrupted' as const } : turn,
    );
  } catch (error) {
    // Storage disabled by the browser (a private window), or a value that
    // is not JSON: no history is a safe degradation, never a thrown error.
    if (error instanceof DOMException || error instanceof SyntaxError) return [];
    throw error;
  }
}

function writeTurns(campaignId: string, turns: TurnRecord[]): void {
  try {
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${campaignId}`, JSON.stringify(turns));
  } catch (error) {
    if (!(error instanceof DOMException)) throw error;
  }
}

function deltaTextOf(turn: TurnRecord): string {
  return turn.events
    .filter((event): event is Extract<TurnEvent, { kind: 'delta' }> => event.kind === 'delta')
    .map((event) => event.data.text)
    .join('');
}

/** Only a completed turn stands as an answered exchange; a refused or
 *  interrupted turn asked a question the model never actually answered, so
 *  it is not replayed into the next turn's history as if it had. */
function historyFrom(turns: readonly TurnRecord[]): TurnRequest['history'] {
  return turns
    .filter((turn) => turn.status === 'done')
    .flatMap((turn) => [
      { role: 'user' as const, content: turn.question },
      { role: 'assistant' as const, content: deltaTextOf(turn) },
    ]);
}

export interface UseConversationArgs {
  campaignId: string;
  tenantId: string;
  window: TurnWindow | null;
  /** The post named by `?post=`, when the assistant opened scoped to one. */
  postId?: string | null;
  /** Overridden in tests; defaults to the real authenticated stream. */
  openStream?: typeof openTurnStream;
}

export interface UseConversationResult {
  turns: TurnRecord[];
  busy: boolean;
  ask: (question: string) => void;
  askAgain: (turnId: string) => void;
  /** Drops every turn and aborts the one still streaming, if any. */
  reset: () => void;
}

export function useConversation(args: UseConversationArgs): UseConversationResult {
  const { campaignId, tenantId, window: turnWindow, postId = null } = args;
  const openStream = args.openStream ?? openTurnStream;
  const [turns, setTurns] = useState<TurnRecord[]>(() => readTurns(campaignId));
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => {
    writeTurns(campaignId, turns);
  }, [campaignId, turns]);

  const runTurn = useCallback(
    (id: string, question: string, history: TurnRequest['history']) => {
      const turn: TurnRequest = {
        tenant_id: tenantId,
        question,
        history,
        window: turnWindow,
        post_id: postId,
      };
      const controller = new AbortController();
      inFlight.current = controller;
      void (async () => {
        try {
          const events = await openStream(campaignId, turn, { signal: controller.signal });
          for await (const event of events) {
            if (controller.signal.aborted) break;
            setTurns((prev) =>
              prev.map((t) =>
                t.id === id
                  ? {
                      ...t,
                      events: [...t.events, event],
                      status:
                        event.kind === 'done'
                          ? 'done'
                          : event.kind === 'refusal'
                            ? 'refused'
                            : t.status,
                    }
                  : t,
              ),
            );
          }
        } catch {
          // Falls through to the status check below: a turn that never
          // reached `done`/`refusal` -- whether the stream threw outright or
          // simply ended -- is still `streaming` at this point, and that is
          // the one signal that matters, not which of the two happened.
        } finally {
          if (inFlight.current === controller) inFlight.current = null;
          setTurns((prev) =>
            prev.map((t) =>
              t.id === id && t.status === 'streaming' ? { ...t, status: 'interrupted' } : t,
            ),
          );
        }
      })();
    },
    [campaignId, tenantId, turnWindow, postId, openStream],
  );

  const busy = turns.some((turn) => turn.status === 'streaming');

  const ask = useCallback(
    (question: string) => {
      if (busy) return;
      const id = crypto.randomUUID();
      const history = historyFrom(turns);
      setTurns((prev) => [...prev, { id, question, events: [], status: 'streaming' }]);
      runTurn(id, question, history);
    },
    [busy, turns, runTurn],
  );

  const askAgain = useCallback(
    (turnId: string) => {
      if (busy) return;
      const index = turns.findIndex((turn) => turn.id === turnId);
      if (index === -1) return;
      const target = turns[index];
      if (target === undefined) return;
      const history = historyFrom(turns.slice(0, index));
      setTurns((prev) =>
        prev.map((turn) =>
          turn.id === turnId ? { ...turn, events: [], status: 'streaming' } : turn,
        ),
      );
      runTurn(turnId, target.question, history);
    },
    [busy, turns, runTurn],
  );

  const reset = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    setTurns([]);
  }, []);

  return { turns, busy, ask, askAgain, reset };
}
